import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { intlShape, injectIntl } from "react-intl";
import moment from "moment";
import humps from "humps";
import { parseTemplate } from "cxa-utils";
import { get, isEmpty, isArray, isObject } from "lodash";
import { Loader } from "cxa-components";

import BenefitsData from "./BenefitsData";
import { load as loadBenefitSummary } from "actions/benefitsActions";
import messages from "components/messagesDescription";
import { FileDownloadIcon } from "cxa-icons";
import { Link } from "react-router-dom";
import { Button } from "cxa-components";
import { downloadFile } from "cxa-utils";

const formatCurrency = (value, currency, intl) => {
  if (typeof value === "undefined") {
    return "-";
  }
  if (value === "999999999999.00") {
    return intl.formatMessage({ id: "Unlimited", defaultMessage: "Unlimited" });
  }
  return `${currency} ${intl.formatNumber(value)}`;
};

const getFormattedValueMap = (row, intl) => ({
  date: value => (value ? intl.formatDate(value) : ""),
  currency: value => (null !== value ? formatCurrency(value, row.currency, intl) : ""),
});

const Statement = ({ intl, planYearId }) => {
  const dispatch = useDispatch();
  const { benefitSummaries, error, fetching } = useSelector(state => state.benefits);

  useEffect(() => {
    if (planYearId) {
      dispatch(loadBenefitSummary({ defaultPlanId: planYearId }));
    }
  }, [dispatch, planYearId]);

  const { formatMessage } = intl;

  const columns = get(benefitSummaries, "pageFields");
  const rows = benefitSummaries.benefits;
  const otherBenefits = benefitSummaries.otherBenefits;

  const benefitStatementConfirmMessageForDisplay =
    benefitSummaries.benefitStatementConfirmMessage;

  const listItem = (item, type) => {
    const valuesArr = Object.values(item);
    const filteredArr = valuesArr.filter(value => value !== "");
    const defaultValue = filteredArr[0];

    if (type === "List" && filteredArr.length <= 1) {
      return null;
    }

    if (type === "List" && filteredArr.length > 1) {
      return valuesArr.map(val => val).join(" : ");
    }

    return defaultValue;
  };

  const listItems = (value = [], type) => {
    let isEmptyList = false;
    const checkIfListIsEmpty = items => {
      if (!items) {
        isEmptyList = true;
      }
      return items;
    };

    const renderListItems = (
      <ul>
        {value.map((val, i) => (
          <li key={`li${i}`}>
            {isObject(val)
              ? checkIfListIsEmpty(listItem(val, type))
              : intl.formatMessage({
                  id: val,
                  defaultMessage: val,
                })}
          </li>
        ))}
      </ul>
    );

    if (isEmptyList) {
      return null;
    }

    return renderListItems;
  };

  const benefitHeaders =
    columns &&
    columns
      .map(
        field =>
          field.shouldDisplay &&
          intl.formatMessage({
            id: field.displayLabel,
            defaultMessage: field.displayLabel,
          })
      )
      .filter(h => h);

  const benefitRows =
    rows &&
    rows.map(
      ben =>
        columns &&
        columns.map(column => {
          if (!column.shouldDisplay) {
            return null;
          }

          const isEmptyFormat = isEmpty(column.format);
          const value = ben[humps.camelize(column.fieldID)];
          let displayLabel = !isEmptyFormat
            ? parseTemplate(column.format, getFormattedValueMap(ben, intl))(ben)
            : "";

          if (isEmptyFormat && value && isArray(value)) {
            displayLabel = value.length > 0 && listItems(value, column.type);
          }

          if (isEmptyFormat && value && !isArray(value)) {
            displayLabel = intl.formatMessage({
              id: value,
              defaultMessage: value,
            });
          }

          if (displayLabel === "None" && column.fieldID === "SelectedPlan") {
            displayLabel = intl.formatMessage(messages.noCoverageDescription);
          }

          if (!value && column.fieldID === "CurrentPlan") {
            return " ";
          }
      

          if (value && column.type === "File") {
            const id = column.fieldID;
            if (id === "ProductSummaryUrl") {
              displayLabel = (
                <Button
                  type="button"
                  tertiary
                  icon
                  onClick={() => downloadFile(ben["".concat(id[0].toLowerCase(), id.substr(1))])}
                >
                  <img alt="" src="/assets/images/common/ProductSummary.png" />
                </Button>
              );
            } else {
              displayLabel = value.length > 0 && (
                <ul>
                  {value.map(({ downloadUrl, fileName, createOn }, i) => (
                    <li key={`li${i}`}>
                      {moment(createOn).format()}{" "}
                      <Link to={downloadUrl} target="_blank" rel="noopener noreferrer">
                        {fileName} <FileDownloadIcon />
                      </Link>
                    </li>
                  ))}
                </ul>
              );
            }
          }

          return displayLabel;
        })
    );

  const reportContent = fetching ? (
    <Loader />
  ) : (
    <BenefitsData
      headers={benefitHeaders}
      rows={benefitRows}
      benefits={rows}
      planYearId={planYearId}
      otherBenefits={otherBenefits}
    />
  );

  return (
    <div>
      {benefitStatementConfirmMessageForDisplay && (
        <div
          className="alert alert--info"
          dangerouslySetInnerHTML={{
            __html: formatMessage({
              id: benefitStatementConfirmMessageForDisplay,
              defaultMessage: benefitStatementConfirmMessageForDisplay,
            }),
          }}
        />
      )}
      <div className="apl-benefit-statement">
        <header>
          <section>
            {`${formatMessage(messages.periodDescription)}:
            ${moment(benefitSummaries.planPeriod && benefitSummaries.planPeriod.startDate).format()} -
            ${moment(benefitSummaries.planPeriod && benefitSummaries.planPeriod.endDate).format()}`}
          </section>
          <section>
            {`${formatMessage(messages.statementDateDescription)}:
            ${moment(benefitSummaries.statementDate).format()}`}
          </section>
        </header>
        {reportContent}
      </div>

      {benefitSummaries.noteForProduct && (
        <div
          dangerouslySetInnerHTML={{
            __html: formatMessage({
              id: benefitSummaries.noteForProduct,
              defaultMessage: benefitSummaries.noteForProduct,
            }),
          }}
        />
      )}
      {error && <p>{error.message}</p>}
    </div>
  );
};

Statement.propTypes = {
  intl: intlShape.isRequired,
  planYearId: PropTypes.string.isRequired
};

export default injectIntl(Statement);


// WEBPACK FOOTER //
// ./components/employee/benefits/Statement.js